import { createTheme } from '@mui/material/styles';

/**
 * Surfaces the default MUI palette can't express.
 *
 * `panel` is the key one: the hero, contact, and footer are dark in *both*
 * color schemes by design, which is why all the white-alpha text sitting on
 * them stays correct without any per-mode branching.
 */
interface SurfacePalette {
  /** Alternating grey section band. */
  band: string;
  /** Permanently dark sections: hero, contact, footer. */
  panel: string;
  /** Hairline borders drawn on top of `panel`. */
  panelBorder: string;
  /** Subtle fill for tag chips. Alpha in dark so it works on any surface. */
  chip: string;
  /** AppBar at rest / after scrolling past the fold. */
  nav: string;
  navScrolled: string;
  /** Mobile drawer. */
  drawer: string;
  /** Card hover shadow color, composed into a box-shadow at the call site. */
  cardShadowColor: string;
  /** Tinted accent fill and border, used by cards and the certification chip. */
  accentSoft: string;
  accentBorder: string;
  /** The oversized section numeral. Needs more alpha in dark to stay readable. */
  sectionNumber: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    surface: SurfacePalette;
  }
  interface PaletteOptions {
    surface?: SurfacePalette;
  }
  interface TypeText {
    /** Muted grey for uppercase eyebrow labels, periods, and metadata. */
    tertiary: string;
  }
}

/**
 * The system font stack. On Apple devices `-apple-system` resolves to San
 * Francisco, the typeface Apple's own sites use, with sensible native
 * equivalents elsewhere. No webfont request, so no FOUT and nothing to load.
 */
// Deliberately no explicit "SF Pro Text"/"SF Pro Display" entry: `-apple-system`
// resolves to the right optical size per font size on its own, whereas naming a
// variant would pin large headings to the small-text cut.
const FONT_FAMILY = [
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(', ');

const theme = createTheme({
  // Explicit attribute name (rather than the 'data' shorthand, which would emit
  // `[data-light]` / `[data-dark]`) so the pre-paint script in index.html can
  // set the very same attribute and avoid a flash of the wrong theme.
  cssVariables: { colorSchemeSelector: '[data-mui-color-scheme="%s"]' },
  colorSchemes: {
    light: {
      palette: {
        primary: { main: '#0071e3', dark: '#0077ed' },
        background: { default: '#ffffff', paper: '#ffffff' },
        text: { primary: '#1d1d1f', secondary: '#6e6e73', tertiary: '#86868b' },
        divider: '#d2d2d7',
        surface: {
          band: '#f5f5f7',
          panel: '#1d1d1f',
          panelBorder: 'rgba(255,255,255,0.12)',
          chip: '#f5f5f7',
          nav: 'rgba(29,29,31,0.6)',
          navScrolled: 'rgba(29,29,31,0.88)',
          drawer: 'rgba(29,29,31,0.97)',
          cardShadowColor: 'rgba(0,0,0,0.07)',
          accentSoft: 'rgba(0,113,227,0.1)',
          accentBorder: 'rgba(0,113,227,0.3)',
          sectionNumber: '#d2d2d7',
        },
      },
    },
    dark: {
      palette: {
        // #0071e3 is too dim against black; these are Apple's dark-mode blues.
        primary: { main: '#2997ff', dark: '#5aaeff' },
        // `paper` sits *lighter* than `band` so project and tool cards keep
        // reading as raised above the section they sit on, as they do in light.
        background: { default: '#0f0f10', paper: '#1d1d1f' },
        text: { primary: '#f5f5f7', secondary: '#a1a1a6', tertiary: '#86868b' },
        divider: 'rgba(255,255,255,0.14)',
        surface: {
          band: '#161617',
          panel: '#000000',
          panelBorder: 'rgba(255,255,255,0.12)',
          chip: 'rgba(255,255,255,0.07)',
          nav: 'rgba(0,0,0,0.6)',
          navScrolled: 'rgba(0,0,0,0.88)',
          drawer: 'rgba(0,0,0,0.97)',
          cardShadowColor: 'rgba(0,0,0,0.55)',
          accentSoft: 'rgba(41,151,255,0.14)',
          accentBorder: 'rgba(41,151,255,0.4)',
          sectionNumber: 'rgba(255,255,255,0.28)',
        },
      },
    },
  },
  typography: {
    fontFamily: FONT_FAMILY,
    // San Francisco carries much more weight at a given size than the serif this
    // replaced, so headings drop to 600 and tighten their tracking: the large,
    // semibold, negatively-tracked look Apple uses for display type.
    h1: { fontWeight: 600, letterSpacing: '-0.035em', lineHeight: 1.05 },
    h2: { fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.1 },
    h3: { fontWeight: 600, letterSpacing: '-0.022em' },
    body1: { lineHeight: 1.55 },
    body2: { lineHeight: 1.55 },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textSizeAdjust: '100%',
        },
        body: {
          overflowX: 'hidden',
          lineHeight: 1.5,
          transition: 'background-color 0.3s ease',
        },
        a: {
          textDecoration: 'none',
          color: 'inherit',
        },
        'img, svg': {
          display: 'block',
          maxWidth: '100%',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '980px',
          textTransform: 'none',
          fontWeight: 500,
          letterSpacing: '-0.01em',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
          fontWeight: 500,
          letterSpacing: '0.01em',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
  },
});

export default theme;
